import { API_URL } from '../config/api';
import { getAccessToken } from './supabaseClient';

export const NEWSLETTER_CONSENT_VERSION = 'newsletter-v1';

const PENDING_KEY = 'upscale_newsletter_optin';
const PENDING_MAX_AGE_MS = 20 * 60 * 1000;

function tokenUserId(token) {
  try {
    const encoded = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(encoded)).sub || null;
  } catch {
    return null;
  }
}

async function request(path, options = {}, expectedUserId = null) {
  const token = await getAccessToken();
  if (!token) throw new Error('Sign in to manage your newsletter subscription.');
  if (expectedUserId && tokenUserId(token) !== expectedUserId) {
    throw new Error('Your account changed. Reload your newsletter preferences.');
  }
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
    },
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(data?.error || data?.message || 'Newsletter preferences are unavailable.');
  }
  return data;
}

export function getNewsletterPreferences(expectedUserId) {
  return request('/api/newsletter/preferences', {}, expectedUserId);
}

export function updateNewsletterPreferences(subscribed, consentVersion, expectedUserId) {
  return request('/api/newsletter/preferences', {
    method: 'PUT',
    body: JSON.stringify({
      subscribed: Boolean(subscribed),
      ...(subscribed && consentVersion ? { consent_version: consentVersion } : {}),
    }),
  }, expectedUserId);
}

export function rememberNewsletterOptIn(scope, userId, state = 'awaiting-auth') {
  if (!scope || !userId || !['awaiting-auth', 'retry'].includes(state)) return;
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify({ scope, userId, state, at: Date.now() }));
  } catch {}
}

export function clearNewsletterOptIn(scope) {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return;
    const pending = JSON.parse(raw);
    if (!scope || pending?.scope === scope) localStorage.removeItem(PENDING_KEY);
  } catch {
    try {
      localStorage.removeItem(PENDING_KEY);
    } catch {}
  }
}

export function takeNewsletterOptIn(scope, userId, state = 'awaiting-auth', now = Date.now()) {
  let pending = null;
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (raw) pending = JSON.parse(raw);
  } catch {
    clearNewsletterOptIn();
    return false;
  }
  const at = Number(pending?.at);
  if (
    !pending ||
    pending.scope !== scope ||
    pending.userId !== userId ||
    pending.state !== state ||
    !Number.isFinite(at) ||
    at > now ||
    now - at >= PENDING_MAX_AGE_MS
  ) {
    clearNewsletterOptIn();
    return false;
  }
  clearNewsletterOptIn(scope);
  return true;
}

export function hasPendingNewsletterOptIn(userId, now = Date.now()) {
  try {
    const pending = JSON.parse(localStorage.getItem(PENDING_KEY) || 'null');
    const at = Number(pending?.at);
    return Boolean(
      pending?.scope === 'retry' &&
      pending?.state === 'retry' &&
      pending?.userId === userId &&
      Number.isFinite(at) &&
      at <= now &&
      now - at < PENDING_MAX_AGE_MS
    );
  } catch {
    return false;
  }
}

export async function subscribeToNewsletter() {
  return updateNewsletterPreferences(true, NEWSLETTER_CONSENT_VERSION);
}

export async function subscribeEmailToNewsletter(email, website = '') {
  const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, website }),
  });
  if (!response.ok) throw new Error('Something went wrong. Please try again.');
  return response.json().catch(() => ({ ok: true }));
}

export async function retryNewsletterOptIn(userId) {
  if (!takeNewsletterOptIn('retry', userId, 'retry')) return null;
  try {
    return await updateNewsletterPreferences(true, NEWSLETTER_CONSENT_VERSION, userId);
  } catch (err) {
    rememberNewsletterOptIn('retry', userId, 'retry');
    throw err;
  }
}
